import React from "react"
import { Employee } from "../../../types/Employee"
import useRoutingInfo from "../../../components/employees/hooks/useRoutingInfo"
import useEmployeeConditionAttributes from "../../../components/employees/hooks/useEmployeeConditionAttributes"
import ErrorWrapper from "../../../components/ErrorWrapper"
import { useGetPastRoutingInfo } from "../../../components/employees/hooks/useGetPastRoutingInfo"
import { RoutingInfoDetail } from "components/routinginfo/RoutingInfoDetail"
import { ChakraProvider, VStack } from "@chakra-ui/react"
import Exoskeleton from "components/Exoskeleton"
import { MemberIndicatedFollowing } from "components/routinginfo/MemberIndicated"
import { Header } from "utils/RoutingInfoStyles"
import useDocumentStatuses from "components/employees/hooks/useDocumentStatuses"
import { useGetAllMedicalQuestionsSkipped } from "../../../components/carrotplans/hooks/useGetAllMedicalQuestionsSkipped"
import { theme } from "../../../utils/theme"
import { Button, ThemeProvider, Box } from "@carrotfertility/carotene-core"
import useGetSprintsCandidacy from "../../../components/routinginfo/hooks/useGetSprintsCandidacy"
import useGetNcqaEnrollment from "../../../components/routinginfo/hooks/useGetNcqaEnrollment"

export class RoutingInfoWrapperProps {
  employee: Employee
}

function CreateCarrotPlanButton({ employeeId }: { employeeId: number }) {
  return (
    <ThemeProvider>
      <Box
        paddingTop={(theme) => theme.spacing(theme.tokens.spacing.xxxl)}
        paddingBottom={(theme) => theme.spacing(theme.tokens.spacing.xxxl)}
      >
        <Button href={`/employees/${employeeId}/carrot-plans/create-plan-v2`} target="_blank">
          Create Carrot Plan
        </Button>
      </Box>
    </ThemeProvider>
  )
}

export default function RoutingInfoWrapper({ employee }: RoutingInfoWrapperProps): JSX.Element {
  const {
    data: currentRoutingInfo,
    isLoading: isCurrentRoutingInfoLoading,
    error: currentRoutingInfoError
  } = useRoutingInfo(employee.employeeId)
  const {
    data: pastRoutingInfo,
    isLoading: isPastRoutingInfoLoading,
    error: pastRoutingInfoError
  } = useGetPastRoutingInfo(employee.employeeId)
  const { data: documentStatuses, error: documentStatusesError } = useDocumentStatuses(employee.employeeId)
  const { data: employeeConditionAttributes, error: empConditionAttrsError } = useEmployeeConditionAttributes(
    employee.employeeId
  )
  const allMedicalQuestionsSkipped = useGetAllMedicalQuestionsSkipped(employee.employeeId)
  const sprintsCandidacyQuery = useGetSprintsCandidacy(employee.employeeId)
  const ncqaEnrollmentQuery = useGetNcqaEnrollment(employee.employeeId)
  const showAboutYou = currentRoutingInfo?.requestSource === "INITIAL_INTAKE" || !currentRoutingInfo?.requestSource
  const areAnyLoading =
    isCurrentRoutingInfoLoading ||
    isPastRoutingInfoLoading ||
    sprintsCandidacyQuery.isLoading ||
    ncqaEnrollmentQuery.isLoading

  return (
    <ChakraProvider theme={theme}>
      <ErrorWrapper
        errors={[
          currentRoutingInfoError,
          empConditionAttrsError,
          pastRoutingInfoError,
          documentStatusesError,
          sprintsCandidacyQuery.error,
          ncqaEnrollmentQuery.error
        ]}
      >
        <VStack spacing={"25px"} align={"start"} shouldWrapChildren={true}>
          <Header>{"Member's latest routing answers"}</Header>
          <MemberIndicatedFollowing
            currentRoutingInfo={currentRoutingInfo}
            allMedicalQuestionsSkipped={allMedicalQuestionsSkipped}
            employeeConditionAttributes={employeeConditionAttributes}
            documentStatuses={documentStatuses}
            sprintsCandidacyStatus={sprintsCandidacyQuery.data}
            ncqaEnrollmentStatus={ncqaEnrollmentQuery.data}
          />
          <Exoskeleton isLoading={areAnyLoading}>
            <RoutingInfoDetail
              currentRoutingInfo={currentRoutingInfo}
              pastRoutingInfo={pastRoutingInfo}
              employee={employee}
              showAboutYou={showAboutYou}
            />
            <CreateCarrotPlanButton employeeId={employee?.employeeId} />
          </Exoskeleton>
        </VStack>
      </ErrorWrapper>
    </ChakraProvider>
  )
}
